import React from 'react';

import { Skeleton, Typography } from '@mui/material';
import clsx from 'clsx';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { RadioSelectedIcon } from 'src/components/icons/RadioSelectedIcon';
import { RadioUnselectedIcon } from 'src/components/icons/RadioUnselectedIcon';
import { WorkflowInviteReviewsIcon } from 'src/components/icons/WorkflowInviteReviewsIcon';
import { WorkflowS4SIcon } from 'src/components/icons/WorkflowS4SIcon';
import { WorkflowSaveDraftIcon } from 'src/components/icons/WorkflowSaveDraftIcon';
import { makeStyles } from 'tss-react/mui';

import { AddCollaboratorsSection } from './AddReviewers';
import { WorkflowCustomEmail } from './CustomEmail';
import { SaveAsDraft } from './SaveAsDraft';
import { StepLayout } from '../StepLayout';

const stepWidthPx = 240;
const useWorkflowStepOptionStyles = makeStyles<{ isActive: boolean; isDisabled: boolean }>()(
  (theme, { isActive, isDisabled }) => ({
    description: {
      marginTop: 'auto',
    },
    disabled: {
      opacity: 0.5,
    },
    iconContainer: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
    },
    step: {
      '&:hover': {
        backgroundColor: !isDisabled ? '#F8F9FA' : undefined,
        border: !isDisabled ? `1px solid ${theme.appColors.primary}` : undefined,
        cursor: !isDisabled ? 'pointer' : undefined,
      },
      backgroundColor: isActive ? '#F8F9FA' : undefined,
      border: `1px solid ${isActive ? theme.appColors.primary : theme.appColors.rule}`,
      borderRadius: 16,
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      gap: theme.spacing(2),
      padding: theme.spacing(2),
    },
    title: {
      color: isActive ? theme.appColors.primary : undefined,
    },
  })
);

interface WorkflowStepOptionProps {
  option: StepOption;
  selectedStep: StepOption['id'];
  setSelectedStep: (step: StepOption['id']) => void;
}

export function WorkflowStepOption({
  option,
  selectedStep,
  setSelectedStep,
}: WorkflowStepOptionProps) {
  const isActive = selectedStep === option.id;
  const isDisabled = Boolean(option.disabledReason);
  const { classes } = useWorkflowStepOptionStyles({
    isActive,
    isDisabled,
  });

  return (
    <AlineTooltip arrow placement="top" title={option.disabledReason ?? ''}>
      <div
        className={classes.step}
        data-cy={option.dataCy}
        onClick={() => {
          if (!isDisabled) setSelectedStep(option.id);
        }}
      >
        <div className={classes.iconContainer}>
          <StepIcon option={option} />
          <StepRadioIcon isActive={isActive} isDisabled={isDisabled} />
        </div>
        <Typography
          className={classes.title}
          color={isDisabled ? 'textSecondary' : 'inherit'}
          variant="subtitle1"
        >
          {option.title}
        </Typography>
        <Typography
          className={classes.description}
          color={isDisabled ? 'textSecondary' : 'inherit'}
        >
          {option.description}
        </Typography>
      </div>
    </AlineTooltip>
  );
}

export enum StepOptionId {
  Invite = 'invite',
  Save = 'save',
  Send = 'send',
}

export interface StepOption {
  dataCy: string;
  description: string;
  disabledReason: string;
  id: StepOptionId;
  onContinue: () => void;
  title: string;
}

export function SelectedWorkflowStep({
  selectedStep,
  workflowId,
}: {
  selectedStep: StepOption['id'];
  workflowId: UUID;
}) {
  switch (selectedStep) {
    case StepOptionId.Send:
      return <WorkflowCustomEmail workflowId={workflowId} />;
    case StepOptionId.Invite:
      return <AddCollaboratorsSection workflowId={workflowId} />;
    case StepOptionId.Save:
      return <SaveAsDraft workflowId={workflowId} />;
  }
}

const useRadioStyles = makeStyles()(() => ({
  disabled: {
    opacity: 0.5,
  },
}));

function StepRadioIcon({ isActive, isDisabled }: { isActive: boolean; isDisabled?: boolean }) {
  const { classes } = useRadioStyles();

  if (isActive) return <RadioSelectedIcon className={clsx(isDisabled && classes.disabled)} />;

  return <RadioUnselectedIcon className={clsx(isDisabled && classes.disabled)} />;
}

export function StepIcon({ option }: { option: StepOption }) {
  const { classes } = useRadioStyles();
  const isDisabled = Boolean(option.disabledReason);

  switch (option.id) {
    case StepOptionId.Send:
      return <WorkflowS4SIcon className={clsx(isDisabled && classes.disabled)} />;
    case StepOptionId.Invite:
      return <WorkflowInviteReviewsIcon className={clsx(isDisabled && classes.disabled)} />;
    case StepOptionId.Save:
      return <WorkflowSaveDraftIcon className={clsx(isDisabled && classes.disabled)} />;
  }
}

const useSkeletonStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
  iconContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  root: {
    backgroundColor: theme.appColors.background,
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  step: {
    border: theme.appBorders.outline,
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    width: stepWidthPx,
  },
  stepLayout: {
    width: 'inherit',
  },
  stepsContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    width: '100%',
  },
}));

export function WorkflowStepOptionLoading() {
  const { classes } = useSkeletonStyles();

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <StepLayout className={classes.stepLayout} title="Choose your next step">
          <div className={classes.stepsContainer}>
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className={classes.step}>
                <div className={classes.iconContainer}>
                  <Skeleton height={32} variant="rectangular" width={32} />
                  <Skeleton height={24} variant="circular" width={24} />
                </div>
                <Skeleton height={24} variant="text" width={100} />
                <div>
                  <Skeleton height={24} variant="text" width={160} />
                  <Skeleton height={24} variant="text" width={120} />
                </div>
              </div>
            ))}
          </div>
        </StepLayout>
      </div>
    </div>
  );
}
